import React from 'react';
import { Box, Grid, CircularProgress } from '@mui/material';
import { colors, spacing } from '../constants';
import { useSLADashboardState } from '../hooks/pages/slaDashboard/useSLADashboardState';
import { useSLADashboardData } from '../hooks/pages/slaDashboard/useSLADashboardData';
import { useSLAMetrics } from '../hooks/pages/slaDashboard/useSLAMetrics';
import { useSLAStatusDistribution } from '../hooks/pages/slaDashboard/useSLAStatusDistribution';
import { useSLAByPriority } from '../hooks/pages/slaDashboard/useSLAByPriority';
import { useSLATrendData } from '../hooks/pages/slaDashboard/useSLATrendData';
import { useSLAQueriesTable } from '../hooks/pages/slaDashboard/useSLAQueriesTable';
import { useProjectsCatalog } from '../hooks/useProjectsCatalog';
import PageHeader from '../components/slaDashboard/PageHeader';
import MetricsCard from '../components/slaDashboard/MetricsCard';
import SLATable from '../components/slaDashboard/SLATable';
import SLATrendChart from '../components/slaDashboard/SLATrendChart';
import SLAPriorityChart from '../components/slaDashboard/SLAPriorityChart';
import StatusDistributionCards from '../components/slaDashboard/StatusDistributionCards';

const SLADashboard = () => {
  // Initialize all hooks
  const dashboardState = useSLADashboardState();
  const projectsCatalog = useProjectsCatalog(true);

  const { fetchQueries } = useSLADashboardData(
    dashboardState.setQueries,
    dashboardState.setLoading,
    dashboardState.setNow
  );

  const slaMetrics = useSLAMetrics(dashboardState.queries, dashboardState.now);
  const statusDistribution = useSLAStatusDistribution(slaMetrics);
  const slaByPriority = useSLAByPriority(dashboardState.queries, dashboardState.now);
  const slaTrendData = useSLATrendData(dashboardState.queries, dashboardState.now);
  
  const { queriesWithSLA, paginatedData } = useSLAQueriesTable(
    dashboardState.queries,
    dashboardState.now,
    dashboardState.page,
    dashboardState.rowsPerPage,
    projectsCatalog
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: colors.background, width: '100%', p: spacing.md }}>
      <Box sx={{ width: '100%', maxWidth: 'none' }}>
        <PageHeader onRefresh={fetchQueries} loading={dashboardState.loading} />

        {dashboardState.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <CircularProgress size={48} />
          </Box>
        ) : (
          <>
            {/* Metrics Cards */}
            <Box
              sx={{
                display: 'flex',
                gap: spacing.sm,
                mb: spacing.sm,
                width: '100%',
                flexWrap: { xs: 'wrap', md: 'nowrap' },
              }}
            >
              <MetricsCard
                label="Overall SLA Adherence"
                value={`${slaMetrics.overallAdherence}%`}
                color={colors.success}
                showProgress={true}
                progressValue={slaMetrics.overallAdherence}
              />
              <MetricsCard
                label="Queries Within SLA"
                value={slaMetrics.queriesWithinSLA.toLocaleString()}
                subValue={slaMetrics.totalQueries > 0 ? `${((slaMetrics.queriesWithinSLA / slaMetrics.totalQueries) * 100).toFixed(1)}% of total` : '0.0% of total'}
                color={colors.success}
              />
              <MetricsCard
                label="SLA Breached"
                value={slaMetrics.queriesBreached}
                subValue={slaMetrics.totalQueries > 0 ? `${((slaMetrics.queriesBreached / slaMetrics.totalQueries) * 100).toFixed(1)}% of total` : '0.0% of total'}
                color={colors.error}
              />
              <MetricsCard
                label="Avg Response Time"
                value={slaMetrics.averageResponseTime}
                subValue="Within target"
                color={colors.headerColor}
              />
              <MetricsCard
                label="Avg Resolution Time"
                value={slaMetrics.averageResolutionTime}
                subValue="Within target"
                color={colors.headerColor}
              />
            </Box>

            {/* SLA Table */}
            <SLATable
              queries={paginatedData}
              loading={dashboardState.loading}
              page={dashboardState.page}
              rowsPerPage={dashboardState.rowsPerPage}
              total={queriesWithSLA.length}
              onPageChange={dashboardState.handleChangePage}
              onRowsPerPageChange={dashboardState.handleChangeRowsPerPage}
            />

            {/* Trend Chart */}
            <Grid container spacing={spacing.sm} sx={{ mb: spacing.sm, width: '100%', mx: 0 }}>
              <SLATrendChart data={slaTrendData} />
            </Grid>

            {/* Status Distribution and Priority Chart */}
            <Box sx={{ display: 'flex', gap: spacing.xs, mb: spacing.xs, width: '100%', flexDirection: { xs: 'column', md: 'row' } }}>
              <StatusDistributionCards statusDistribution={statusDistribution} />
              <SLAPriorityChart data={slaByPriority} />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SLADashboard;
